import React from "react";
import { AlertCircle } from "lucide-react";
import { emitLifecycle } from "@/lib/lifecycle";

/**
 * GlobalErrorBoundary — top-level React error boundary mounted above the
 * route renderer in App.js. Catches any uncaught render error from ANY
 * page so users never see a white screen.
 *
 * Phase 6.AH — added after the "MU still fails on the frontend even
 * though backend returns 200" investigation. The page-level
 * AnalyzeErrorBoundary catches the analyze result subtree, but a crash
 * OUTSIDE that subtree (a useEffect side-effect, the header, the
 * paywall modal, etc.) would still surface as the legacy "Something
 * went wrong" or a blank page. This boundary is the last line of defense.
 *
 * Behavior:
 *   * On caught error:
 *      1) Renders a clean recovery UI with retry CTA
 *      2) POSTs a structured crash report to /api/_health/frontend_error
 *         so admins can see exactly which component crashed without
 *         needing browser devtools access
 *      3) Shows admin users the full error + React component stack +
 *         backend audit_id of the reported crash for cross-correlation
 */
export default class GlobalErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null, info: null, auditId: null, reported: false };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  async componentDidCatch(error, info) {
    // eslint-disable-next-line no-console
    console.error("[GlobalErrorBoundary] caught", error, info);
    this.setState({ info });
    // Phase 6.AM — Emit the canonical `error_screen_rendered` lifecycle
    // event whenever a global crash bubbles up. This is the LAST resort
    // signal — if every other lifecycle event is missing but this one
    // appears for ticker MU, the operator instantly knows the crash
    // happened above the Analyze.js subtree (e.g. AuthProvider, header,
    // navbar, paywall portal) — places that Phase 6.AL's analyze-side
    // instrumentation cannot see.
    try {
      const failingComponent = (info?.componentStack || "")
        .split("\n").map((s) => s.trim()).filter(Boolean)[0] || null;
      const path = (typeof window !== "undefined") ? window.location.pathname : null;
      const tickerMatch = path ? path.match(/\/analyze\/([A-Za-z0-9.\-]+)/) : null;
      emitLifecycle("error_screen_rendered", {
        ticker: tickerMatch ? tickerMatch[1].toUpperCase() : null,
        extra: {
          source: "global_error_boundary",
          boundary: this.props.boundaryName || "global",
          error_name: error?.name || "Error",
          error_message: String(error?.message || error || "").slice(0, 500),
          failing_component: failingComponent,
          first_stack_frame: String(error?.stack || "").split("\n")[1]?.trim() || null,
          pathname: path,
        },
      });
    } catch { /* swallow */ }
    // Fire-and-forget POST to the backend. We use the raw fetch API so
    // this works even if the axios `api` client is what crashed.
    try {
      const backend = (typeof process !== "undefined" && process.env?.REACT_APP_BACKEND_URL) || "";
      const url = `${backend}/api/_health/frontend_error`;
      const body = {
        boundary: this.props.boundaryName || "global",
        component: (info?.componentStack || "").split("\n").map((s) => s.trim()).filter(Boolean)[0] || null,
        error_name: error?.name || "Error",
        error_message: String(error?.message || error || "").slice(0, 500),
        error_stack: String(error?.stack || "").slice(0, 4000),
        component_stack: String(info?.componentStack || "").slice(0, 4000),
        user_agent: (typeof navigator !== "undefined") ? navigator.userAgent : null,
        url: (typeof window !== "undefined") ? window.location.href : null,
        extra: { boundary_kind: "global" },
      };
      const res = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(body),
      });
      const json = await res.json().catch(() => ({}));
      this.setState({ auditId: json?.audit_id || null, reported: true });
    } catch (postErr) {
      // eslint-disable-next-line no-console
      console.error("[GlobalErrorBoundary] failed to report", postErr);
      this.setState({ reported: false });
    }
  }

  reset = () => {
    this.setState({ error: null, info: null, auditId: null, reported: false });
  };

  render() {
    if (!this.state.error) return this.props.children;
    const { error, info, auditId, reported } = this.state;
    return (
      <div className="min-h-screen flex items-center justify-center p-6 bg-background">
        <div
          className="max-w-xl w-full rounded-2xl border border-amber-500/40 bg-amber-500/5 p-6"
          data-testid="global-error-boundary"
        >
          <div className="flex items-start gap-3">
            <div className="w-12 h-12 rounded-xl bg-amber-500/15 border border-amber-500/30 flex items-center justify-center flex-shrink-0">
              <AlertCircle className="w-6 h-6 text-amber-600" />
            </div>
            <div className="flex-1">
              <div
                className="text-lg font-semibold text-amber-700 mb-1"
                data-testid="global-error-title"
              >
                Something didn't load correctly
              </div>
              <div className="text-sm text-muted-foreground leading-relaxed">
                We've automatically logged this so the team can fix it. You can
                retry the page, or reload your browser. Your data is safe.
              </div>
              <div className="mt-3 flex items-center gap-2">
                <button
                  type="button"
                  onClick={this.reset}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-amber-500 text-white text-sm font-medium hover:bg-amber-600 transition-colors"
                  data-testid="global-error-retry"
                >
                  Retry
                </button>
                <button
                  type="button"
                  onClick={() => { if (typeof window !== "undefined") window.location.reload(); }}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-amber-500/40 text-amber-700 text-sm font-medium hover:bg-amber-500/10"
                  data-testid="global-error-reload"
                >
                  Reload page
                </button>
              </div>
              {auditId ? (
                <div className="mt-3 text-[11px] text-muted-foreground" data-testid="global-error-audit-id">
                  Trace id:{" "}
                  <code className="font-mono">{auditId}</code>
                </div>
              ) : reported ? null : (
                <div className="mt-3 text-[11px] text-muted-foreground">
                  (Could not reach the backend to log this error)
                </div>
              )}
            </div>
          </div>
          {/* Admin-only details. We can't easily check is_admin from a
              boundary without prop-drilling, so we show this section
              behind a <details> dropdown that admins know to expand. */}
          <details className="mt-4">
            <summary className="cursor-pointer text-xs text-muted-foreground hover:text-foreground">
              Technical details
            </summary>
            <div className="mt-2 space-y-2 text-[11px]">
              <div>
                <span className="text-muted-foreground">Error:</span>{" "}
                <code className="text-rose-700">
                  {error?.name || "Error"}: {String(error?.message || error || "").slice(0, 300)}
                </code>
              </div>
              {error?.stack ? (
                <pre
                  className="whitespace-pre-wrap break-all bg-black/60 text-rose-200 p-2 rounded text-[10px] max-h-72 overflow-auto"
                  data-testid="global-error-stack"
                >
                  {String(error.stack)}
                </pre>
              ) : null}
              {info?.componentStack ? (
                <pre
                  className="whitespace-pre-wrap break-all bg-black/40 text-amber-200 p-2 rounded text-[10px] max-h-72 overflow-auto"
                  data-testid="global-error-component-stack"
                >
                  {String(info.componentStack)}
                </pre>
              ) : null}
            </div>
          </details>
        </div>
      </div>
    );
  }
}
